import LegalPage, { H2, P, UL } from "@/components/LegalPage";
import { BRAND } from "@/config/brand";

export default function Refund() {
  return (
    <LegalPage eyebrow="Legal" title="Refund & Cancellation Policy"
      updated="8 February 2026" testid="refund-page">
      <P>
        We know booking counselling is a big step. This policy explains, in plain
        language, when and how we refund your ₹ payments. All figures are in Indian
        Rupees. All refunds are processed via the original payment method through
        Razorpay.
      </P>

      <H2>1. Right to cancel a booking</H2>
      <P>
        These rules apply to single-session bookings: Initial Consultation (₹900),
        Follow Up (₹600) and any à-la-carte booking. Multi-session packages
        (Growth ₹1,500 and Proper Care ₹7,200) follow Section 5.
      </P>
      <UL>
        <li><strong>More than 24 hours before the scheduled session</strong> — 100% refund. Cancel from your dashboard, or email <a href={`mailto:${BRAND.bookingSupportEmail}`} style={{ color: "var(--brand)" }}>{BRAND.bookingSupportEmail}</a>.</li>
        <li><strong>Between 24 hours and 2 hours before the session</strong> — 50% refund, or free reschedule to a new slot within 30 days.</li>
        <li><strong>Less than 2 hours before the session</strong> — no refund, but a one-time reschedule within 7 days is permitted at our discretion.</li>
      </UL>

      <H2>2. No-shows</H2>
      <P>
        If the client does not join the session within 15 minutes of the scheduled
        start time and does not respond to the counsellor's message, the session is
        marked as a no-show and no refund is issued. Please contact us if there was a
        genuine emergency — we consider such cases on a case-by-case basis.
      </P>

      <H2>3. If the counsellor doesn't show up</H2>
      <P>
        If your counsellor fails to join within 10 minutes of the scheduled start and
        you have been in the room and reachable, you are entitled to a <strong>full
        refund</strong> or a free session at your next preferred slot. Simply email
        us with your booking ID.
      </P>

      <H2>4. Technical failures</H2>
      <P>
        If a session cannot be completed due to a verifiable technical failure on our
        end (video room down, payment double-charge, etc.), we will either reschedule
        the session at no cost or issue a full refund, at your choice. Technical
        issues on the client's side (poor internet, browser blocking permissions, out
        of battery) do not qualify unless you inform us <em>during</em> the scheduled
        window so the counsellor can help.
      </P>

      <H2>5. Package refunds</H2>
      <UL>
        <li><strong>Growth Package (3 sessions — ₹1,500)</strong>: refundable pro-rata for unused sessions if cancelled more than 24 hours before your next scheduled session. Used sessions are billed at the single-session rate (₹600) and deducted from the refund.</li>
        <li><strong>Proper Care (12-month plan — ₹7,200)</strong>: refundable pro-rata within the first 30 days. After 30 days, unused calendar weeks are refunded at ₹150 per remaining week, up to a maximum of the paid amount. Sessions already attended are non-refundable.</li>
      </UL>

      <H2>6. Counsellor membership refunds</H2>
      <UL>
        <li><strong>Earn Everything After — Monthly (₹1,000)</strong>: refundable within 7 days of purchase if no bookings have been taken. After 7 days, non-refundable.</li>
        <li><strong>Earn Everything After — Yearly (₹10,000)</strong>: refundable pro-rata within 30 days of purchase (₹834 per remaining full month). After 30 days, non-refundable.</li>
        <li><strong>Pay Nothing Before</strong>: no upfront charge, therefore no refund is applicable.</li>
      </UL>

      <H2>7. Chargebacks</H2>
      <P>
        Please contact us before raising a chargeback with your bank. In almost every
        case we can resolve the issue faster and without you paying dispute fees.
        Chargebacks raised without first contacting us may lead to suspension of your
        account under Section 12 of the Terms.
      </P>

      <H2>8. Refund timelines</H2>
      <UL>
        <li>Refunds are initiated within 3 working days of an approved request.</li>
        <li>Once initiated, Razorpay typically credits the amount back to your card / UPI / bank in <strong>5–10 working days</strong> depending on your bank.</li>
        <li>You will receive an email confirmation once the refund is initiated.</li>
      </UL>

      <H2>9. How to request a refund</H2>
      <P>
        Email <a href={`mailto:${BRAND.bookingSupportEmail}?subject=Refund%20request`}
           style={{ color: "var(--brand)" }} data-testid="refund-email-link">
          {BRAND.bookingSupportEmail}
        </a> with the subject line &ldquo;Refund request&rdquo; and include:
      </P>
      <UL>
        <li>Your booking ID or Razorpay payment ID</li>
        <li>The reason for the refund</li>
        <li>Your registered email or username</li>
      </UL>

      <H2>10. Contact</H2>
      <P>
        Questions about this policy? Reach us at <a href={`mailto:${BRAND.bookingSupportEmail}`}
           style={{ color: "var(--brand)" }}>{BRAND.bookingSupportEmail}</a>. We reply within one
        business day, Monday to Saturday, 10 AM – 8 PM IST.
      </P>
    </LegalPage>
  );
}
